import { createEffect, createMemo, createSignal, type JSX, on, Show, untrack } from 'solid-js';
import { t } from '../i18n';
import {
  type AcceptedMoves,
  AcceptedMovesContext,
  createAcceptedMoves,
} from '../lib/accepted-move';
import type { ApiBlocker } from '../lib/blockers';
import type { SendAnswer } from '../lib/card-answer';
import { type CardSizes, createCardSizes } from '../lib/card-size';
import { useDebouncedSearch } from '../lib/debounced-search';
import {
  CARDS_PAGE,
  type FleetViewState,
  filterCounts,
  type ListColumn,
  type MachineList,
  machineList,
  moreCards,
  nextCardLimit,
  type PagedGroup,
  pageGroups,
  SEARCH_ABOVE,
  sortPress,
} from '../lib/fleet-machines';
import type { MapMode } from '../lib/fleet-map';
import { listPage, listPageNav } from '../lib/fleet-pager';
import type { UtilBox } from '../lib/fleetUtilization';
import { createPageGuard, FloodGuardContext } from '../lib/flood-guard-context';
import type { KeyRow } from '../lib/keys-types';
import { blockersByTask } from '../lib/machine-card';
import type { MachineHandlers } from '../lib/machine-panel';
import { createMovePage, type MovePage, MovePageContext } from '../lib/move-page';
import type { PageNav } from '../lib/paginate';
import type { SendWorkPage } from '../lib/send-work-page';
import { playAccepted } from '../lib/transfer-capture';
import { createTableViewport } from '../lib/viewport';
import { ControlLockStatus } from './ControlLockStatus';
import { FleetMachinesTools } from './FleetMachinesTools';
import type { MachineGroupsProps } from './MachineGroups';
import { MachineGroups } from './MachineGroups';
import { MachineMap } from './MachineMap';
import { MachineTable } from './MachineTable';
import { CardSizeControl } from './machine-card/CardSizeControl';
import { DragLayer } from './machine-card/DragLayer';
import { MachineCardGrid } from './machine-card/MachineCardGrid';
import type { CardWrites } from './machine-card/MissionCard';
import { PutBackButton } from './machine-card/PutBackButton';
import { Pager } from './Pager';
import { Panel } from './Panel';
import { type TransferSink, TransferStack } from './TransferStack';
import { Button } from './ui/Button';

// The Fleet page's Machines list, Cards view (#6560, prototype `machineSection`): one list
// of every machine, replacing the old Workers and Agents grids. Filter chips with counts,
// search above 10 machines, Group by server, the sort note, then "Card size" and the
// Mission Panel cards (#6785): 12, "Show 12 more" up to 60, then "Switch to List or Map to
// see the whole fleet". Every rule is `lib/fleet-machines.ts`'s and `lib/machine-card.ts`'s;
// the container owns the URL (`view` / `onView`, owner ruling G10), the writes and the one
// open-blockers read the cards join by task.
//
// PAGING RESETS ON A NEW QUESTION, NOT ON NEW DATA: a filter, search, sort or grouping
// change goes back to 12 cards; the 20-second poll keeps what "Show 12 more" opened.
//
// THE LIST VIEW (#6561) pages the same list six at a time under the numbered `<Pager/>`,
// its page in the url (`page` / `onPage`; the reset to page one is the url write's,
// `useFleetView`). From `md` it is `MachineTable`; narrower, the page's machines show as
// Mission Panel cards (the prototype's 760px rule), so only one of the two is ever mounted.
//
// THE MAP VIEW (#6562) draws every machine that passed the filter and search as one square,
// grouped the same way, with no paging; its "Show" switch replaces the sort note. What the
// Map is coloured by is this list's own state, not the url's: G10 names the url's keys
// (`view, filter, q, sort, dir, group, page`) and the colour is not one of them, so a
// shared Map link opens on Health.
//
// THE PAGE'S ONE FLOOD GUARD (#6753): every card control on this page (job Pause, Resume and
// Stop, Pause all, epic Pause, Resume and Stop, the epic model picker; #6766's switches) goes
// through ONE guard, made HERE and handed to every card view (`MachineCardGrid` under
// `MachineGroups`, the List view's narrow cards) through `FloodGuardContext`. A card never
// makes its own: two would each let six changes through and "the 7th locks every control on
// the page" would be false (`lib/flood-guard.ts`). It is disposed with the page, so a change
// still waiting (a Stop inside its Undo window) is then never sent.

export type FleetMachinesProps = {
  readonly boxes: readonly UtilBox[];
  /** This org's open blockers (`useBlockers(() => true)`): a card joins them by its task. */
  readonly blockers: readonly ApiBlocker[];
  /** "Answer here" on a card (#6726): `useBlockerAnswer`, which re-reads the blockers. */
  readonly onAnswer: SendAnswer;
  readonly view: FleetViewState;
  readonly onView: (next: FleetViewState) => void;
  /** The List view's page as the url asks for it (clamped here on read). */
  readonly page: number;
  readonly onPage: (page: number) => void;
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  readonly onManage: MachineHandlers['onManage'];
  readonly onRename: (box: UtilBox) => void;
  readonly onDrain: (box: UtilBox) => void;
  readonly onUndrain: (box: UtilBox) => void;
  readonly onRemove: (box: UtilBox) => void;
  /** A group header's server-name write toasts here and re-reads the utilization query. */
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
  readonly token: string | null;
  /** The page's ONE Send work controller (#6770), handed to every card. */
  readonly send?: SendWorkPage | undefined;
  /** The account's keys for the cards' epic model pickers (#6753); read by the route for an admin. */
  readonly keys?: () => readonly KeyRow[] | undefined;
  /** When the fetch behind `boxes` started (`read_started_ms`), for the controls' overrides. */
  readonly readAt?: () => number;
  /** The same, in the page's order (`read_order`), for the move budgets (#6772). */
  readonly readOrder?: () => number;
  /**
   * Handed the page's ONE move controller once it is made (#6773): the transfer stack's viewer
   * test drives its accepted-move and refusal hooks through it. Nothing in the app passes it.
   */
  readonly onMovePage?: (page: MovePage) => void;
};

type FleetMachinesBodyProps = {
  readonly boxes: FleetMachinesProps['boxes'];
  readonly view: FleetMachinesProps['view'];
  readonly canWrite: FleetMachinesProps['canWrite'];
  readonly onManage: FleetMachinesProps['onManage'];
  readonly onRename: FleetMachinesProps['onRename'];
  readonly onDrain: FleetMachinesProps['onDrain'];
  readonly onUndrain: FleetMachinesProps['onUndrain'];
  readonly onRemove: FleetMachinesProps['onRemove'];
  readonly notify: FleetMachinesProps['notify'];
  readonly onWritten: FleetMachinesProps['onWritten'];
  readonly token: FleetMachinesProps['token'];
  readonly setLimit: (up: (n: number) => number) => void;
  readonly set: (patch: Partial<FleetViewState>) => void;
};

/**
 * What every Mission Panel card is handed beyond its machine: the joins, the size, the answer
 * call and the page's Send work controller (#6770).
 */
type CardDeps = {
  readonly byTask: ReadonlyMap<string, readonly ApiBlocker[]>;
  readonly sizes: CardSizes;
  readonly onAnswer: SendAnswer;
  readonly send: SendWorkPage | undefined;
  /** What the cards' job and epic controls write with (#6753). */
  readonly writes: CardWrites;
};

/** What the three views render through: the page and card cuts, the sort and the search box. */
type FleetMachinesViews = {
  readonly list: () => MachineList;
  readonly byId: () => ReadonlyMap<string, UtilBox>;
  readonly page: () => readonly PagedGroup[];
  readonly paged: () => readonly PagedGroup[];
  readonly nav: PageNav;
  readonly wide: () => boolean;
  readonly onSort: (column: ListColumn) => void;
  readonly shown: () => number;
  readonly mapMode: () => MapMode;
  readonly setMapMode: (mode: MapMode) => void;
  readonly qInput: () => string;
  readonly onQuery: (next: string) => void;
  readonly carded: () => boolean;
  readonly mapped: () => readonly PagedGroup[];
  readonly cards: CardDeps;
};

type CardGridProps = Pick<
  FleetMachinesProps,
  'canWrite' | 'onManage' | 'onRename' | 'onDrain' | 'onUndrain' | 'onRemove' | 'notify'
> & {
  readonly byId: () => ReadonlyMap<string, UtilBox>;
  readonly allBoxes: readonly UtilBox[];
  readonly cards: CardDeps;
};

/** `MachineGroups`' body for the Cards view and the List view's narrow fallback. */
const cardGrid =
  (props: CardGridProps) =>
  (ids: () => readonly string[], label: () => string): JSX.Element => (
    <MachineCardGrid
      ids={ids}
      label={label}
      byId={props.byId()}
      allBoxes={props.allBoxes}
      byTask={props.cards.byTask}
      sizes={props.cards.sizes}
      onAnswer={props.cards.onAnswer}
      send={props.cards.send}
      writes={props.cards.writes}
      canWrite={props.canWrite}
      notify={props.notify}
      onManage={props.onManage}
      onRename={props.onRename}
      onDrain={props.onDrain}
      onUndrain={props.onUndrain}
      onRemove={props.onRemove}
    />
  );

/** The Cards view (#6560) and the Map view (#6562), under the shared toolbar and empty state. */
function FleetCardsAndMap(
  props: Pick<
    FleetMachinesProps,
    'canWrite' | 'onManage' | 'onRename' | 'onDrain' | 'onUndrain' | 'onRemove'
  > &
    Pick<MachineGroupsProps, 'allBoxes' | 'token' | 'notify' | 'onWritten'> & {
      readonly paged: () => readonly PagedGroup[];
      readonly byId: () => ReadonlyMap<string, UtilBox>;
      readonly carded: () => boolean;
      readonly mapMode: () => MapMode;
      readonly mapped: () => readonly PagedGroup[];
      readonly boxes: FleetMachinesProps['boxes'];
      readonly cards: CardDeps;
    },
): JSX.Element {
  return (
    <>
      <Show when={props.carded()}>
        <CardSizeControl sizes={props.cards.sizes} hint={props.canWrite} />
        <MachineGroups
          paged={props.paged()}
          allBoxes={props.allBoxes}
          token={props.token}
          notify={props.notify}
          onWritten={props.onWritten}
          canWrite={props.canWrite}
          body={cardGrid(props)}
        />
      </Show>
      <Show when={!props.carded()}>
        <MachineMap
          paged={props.mapped()}
          byId={props.byId()}
          boxes={props.boxes}
          mode={props.mapMode()}
          canWrite={props.canWrite}
          onManage={props.onManage}
          onRename={props.onRename}
          onDrain={props.onDrain}
          onUndrain={props.onUndrain}
          onRemove={props.onRemove}
          allBoxes={props.allBoxes}
          token={props.token}
          notify={props.notify}
          onWritten={props.onWritten}
        />
      </Show>
    </>
  );
}

/**
 * "Show 12 more" up to 60, then the quiet way out (#6785, prototype `:4788-4795`): the
 * Cards view's growth, reset by a new question.
 */
function FleetMoreCards(props: {
  readonly list: () => MachineList;
  readonly shown: () => number;
  readonly setLimit: (up: (n: number) => number) => void;
}): JSX.Element {
  const more = () => moreCards(props.list().total, props.shown());
  const next = (): number | null => {
    const m = more();
    return m?.kind === 'more' ? m.count : null;
  };
  return (
    <Show when={more() !== null}>
      <div class="fleet-machines__more">
        <span>
          {t('fleet.machines.showing', { shown: props.shown(), total: props.list().total })}
        </span>
        <Show
          when={next()}
          fallback={<span class="fleet-machines__cap">{t('fleet.machines.cards_cap')}</span>}
        >
          {(count) => (
            <Button variant="outline" size="sm" onClick={() => props.setLimit(nextCardLimit)}>
              {t('fleet.machines.show_more', { count: count() })}
            </Button>
          )}
        </Show>
      </div>
    </Show>
  );
}

/** The List view (#6561): `MachineTable` from `md`, cards below it, over the numbered pager. */
function FleetListView(
  props: Pick<
    FleetMachinesProps,
    'canWrite' | 'onManage' | 'onRename' | 'onDrain' | 'onUndrain' | 'onRemove'
  > &
    Pick<MachineGroupsProps, 'allBoxes' | 'token' | 'notify' | 'onWritten'> & {
      readonly page: () => readonly PagedGroup[];
      readonly byId: () => ReadonlyMap<string, UtilBox>;
      readonly view: FleetMachinesProps['view'];
      readonly onSort: (column: ListColumn) => void;
      readonly nav: PageNav;
      readonly wide: () => boolean;
      readonly cards: CardDeps;
    },
): JSX.Element {
  return (
    <>
      <Show
        when={props.wide()}
        fallback={
          <MachineGroups
            paged={props.page()}
            allBoxes={props.allBoxes}
            token={props.token}
            notify={props.notify}
            onWritten={props.onWritten}
            canWrite={props.canWrite}
            body={cardGrid(props)}
          />
        }
      >
        <MachineTable
          paged={props.page()}
          byId={props.byId()}
          view={props.view}
          onSort={props.onSort}
          canWrite={props.canWrite}
          onManage={props.onManage}
          onRename={props.onRename}
          onDrain={props.onDrain}
          onUndrain={props.onUndrain}
          onRemove={props.onRemove}
          allBoxes={props.allBoxes}
          token={props.token}
          notify={props.notify}
          onWritten={props.onWritten}
        />
      </Show>
      <Pager label={t('fleet.machines.heading')} pagination={props.nav} />
    </>
  );
}

/** The machines themselves, once there are any: the toolbar, the one active view, the pager. */
function FleetMachinesBody(
  props: FleetMachinesBodyProps & { readonly views: FleetMachinesViews },
): JSX.Element {
  const v = props.views;
  return (
    <>
      <FleetMachinesTools
        view={props.view}
        set={props.set}
        counts={filterCounts(props.boxes)}
        grouped={v.list().groups !== null}
        // Above 10 machines, and always while a search is set: hiding the box would leave a
        // filter nobody can see or clear.
        searchable={props.boxes.length > SEARCH_ABOVE || props.view.q !== ''}
        query={v.qInput()}
        onQuery={v.onQuery}
        mapMode={v.mapMode()}
        onMapMode={v.setMapMode}
      />
      {/* On its own gate, in every view (#6772 review round 4). */}
      <PutBackButton canWrite={props.canWrite} />
      <Show
        when={v.list().total > 0}
        fallback={
          <p class="fleet-machines__none" role="status">
            {t('fleet.machines.none')}{' '}
            <Button variant="link" size="sm" onClick={() => props.set({ filter: 'all', q: '' })}>
              {t('fleet.machines.none_show_all')}
            </Button>
          </p>
        }
      >
        <Show when={props.view.view === 'list'}>
          <FleetListView
            page={v.page}
            byId={v.byId}
            view={props.view}
            onSort={v.onSort}
            nav={v.nav}
            wide={v.wide}
            cards={v.cards}
            canWrite={props.canWrite}
            onManage={props.onManage}
            onRename={props.onRename}
            onDrain={props.onDrain}
            onUndrain={props.onUndrain}
            onRemove={props.onRemove}
            allBoxes={props.boxes}
            token={props.token}
            notify={props.notify}
            onWritten={props.onWritten}
          />
        </Show>
        <Show when={v.carded() || props.view.view === 'map'}>
          <FleetCardsAndMap
            paged={v.paged}
            byId={v.byId}
            carded={v.carded}
            mapMode={v.mapMode}
            mapped={v.mapped}
            boxes={props.boxes}
            cards={v.cards}
            canWrite={props.canWrite}
            onManage={props.onManage}
            onRename={props.onRename}
            onDrain={props.onDrain}
            onUndrain={props.onUndrain}
            onRemove={props.onRemove}
            allBoxes={props.boxes}
            token={props.token}
            notify={props.notify}
            onWritten={props.onWritten}
          />
        </Show>
        <Show when={v.carded()}>
          <FleetMoreCards list={v.list} shown={v.shown} setLimit={props.setLimit} />
        </Show>
      </Show>
    </>
  );
}

export function FleetMachines(props: FleetMachinesProps): JSX.Element {
  const guard = createPageGuard();
  // The page's ONE move controller (#6771): a drag crosses cards, so it lives here, beside the
  // guard every move is counted by.
  // What every accepted move leaves behind (#6772): the note, Undo, Put back, tags, rings and
  // the motion, reached from the controller's one send seam.
  let accepted: AcceptedMoves | undefined;
  // The transfer stack's sink (#6773): set only while the stack is mounted, which is only for a
  // person who may move work. A viewer's moves and refusals are forwarded to nothing.
  let transfers: TransferSink | null = null;
  const move = createMovePage({
    guard,
    token: () => props.token,
    boxes: () => props.boxes,
    onWritten: () => props.onWritten(),
    onMoveAccepted: (a, order) => {
      const ref = accepted?.accept(a) ?? null;
      if (ref !== null) transfers?.accepted(a, ref, order);
    },
    onMoveRefused: (r) => transfers?.refused(r),
    // Without a read time a new row object is the newer read (`MovePage.movesOf`).
    ...(props.readOrder === undefined ? {} : { readOrder: () => props.readOrder?.() ?? 0 }),
  });
  accepted = createAcceptedMoves({
    page: () => move,
    token: props.token,
    motion: (a, done) =>
      playAccepted(a, (id) => move.boxOf(id)?.display_name ?? id.slice(0, 8), undefined, done),
  });
  props.onMovePage?.(move);
  const [limit, setLimit] = createSignal(CARDS_PAGE);
  const { qInput, setQInput, debounce } = useDebouncedSearch();
  // Bumped by every keystroke and every outside replacement of the box, so a pending
  // commit knows it is stale and is dropped.
  let searchGen = 0;
  /**
   * THE ONE SEAM where anything but the reader's own typing replaces the search box: a
   * clear from this list (All, "Show all machines") and every outside url change (the
   * Online tile, View server, back / forward). It cancels any pending commit, so a search
   * typed and then cleared inside the debounce window never comes back.
   */
  const replaceSearch = (q: string): void => {
    searchGen += 1;
    setQInput(q);
  };
  const set = (patch: Partial<FleetViewState>): void => {
    // A clear lets go of the box HERE, not only through the url: when the url already says
    // All the write changes nothing and the url effect below never fires.
    if (patch.q === '') replaceSearch('');
    props.onView({ ...props.view, ...patch });
  };

  const byId = createMemo(() => new Map(props.boxes.map((b) => [b.id, b])));
  const list = createMemo(() => machineList(props.boxes, props.view));
  const paged = createMemo(() => pageGroups(list(), limit()));
  const nav = listPageNav({ list, page: () => props.page, goTo: (n) => props.onPage(n) });
  const page = createMemo(() => listPage(list(), nav));
  const wide = createTableViewport();
  const onSort = (column: ListColumn): void => set(sortPress(props.view, column));
  const shown = (): number => paged().reduce((sum, g) => sum + g.rows.length, 0);
  const [mapMode, setMapMode] = createSignal<MapMode>('health');
  const mapped = createMemo(() => pageGroups(list(), Number.POSITIVE_INFINITY));
  const carded = (): boolean => props.view.view === 'cards';
  const byTask = createMemo(() => blockersByTask(props.blockers));
  const sizes = createCardSizes();
  // Each read of the fleet drops the stored sizes of machines that left it (#6785).
  createEffect(() => {
    const ids = props.boxes.map((b) => b.id);
    untrack(() => sizes.prune(ids));
  });

  createEffect(
    on(
      () => [props.view.filter, props.view.q, props.view.sort, props.view.dir, props.view.group],
      () => setLimit(CARDS_PAGE),
      { defer: true },
    ),
  );

  // The search box types into a local buffer and commits to the URL after a pause; a URL
  // change from outside (a tile, View server, back / forward) flows back into the box.
  createEffect(
    on(
      () => props.view.q,
      (q) => {
        if (q !== untrack(qInput).trim()) replaceSearch(q);
      },
    ),
  );
  const onQuery = (next: string): void => {
    setQInput(next);
    searchGen += 1;
    const mine = searchGen;
    // A commit the box has since moved past (a newer keystroke, or a clear) is dropped.
    debounce(next, (q) => {
      if (mine === searchGen) set({ q });
    });
  };

  const views: FleetMachinesViews = {
    list,
    byId,
    page,
    paged,
    nav,
    wide,
    onSort,
    shown,
    mapMode,
    setMapMode,
    qInput,
    onQuery,
    carded,
    mapped,
    cards: {
      get byTask() {
        return byTask();
      },
      sizes,
      onAnswer: (id, reply) => props.onAnswer(id, reply),
      get send() {
        return props.send;
      },
      writes: {
        get token() {
          return props.token;
        },
        onWritten: () => props.onWritten(),
        keys: () => props.keys?.(),
        readAt: () => props.readAt?.() ?? 0,
      },
    },
  };

  return (
    <FloodGuardContext.Provider value={guard}>
      <MovePageContext.Provider value={move}>
        <AcceptedMovesContext.Provider value={accepted}>
          <FleetMachinesPanel {...props} setLimit={setLimit} set={set} views={views} />
          <DragLayer />
          <Show when={props.canWrite}>
            <TransferStack onSink={(sink) => (transfers = sink)} />
          </Show>
        </AcceptedMovesContext.Provider>
      </MovePageContext.Provider>
    </FloodGuardContext.Provider>
  );
}

/** The panel itself, under the page's one guard. */
function FleetMachinesPanel(
  props: FleetMachinesProps & {
    readonly setLimit: (up: (n: number) => number) => void;
    readonly set: (patch: Partial<FleetViewState>) => void;
    readonly views: FleetMachinesViews;
  },
): JSX.Element {
  return (
    <Panel
      class="fleet-machines"
      title={t('fleet.machines.heading')}
      action={<span class="fleet-machines__count">{props.boxes.length}</span>}
    >
      <ControlLockStatus />
      <FleetMachinesBody
        boxes={props.boxes}
        view={props.view}
        canWrite={props.canWrite}
        onManage={props.onManage}
        onRename={props.onRename}
        onDrain={props.onDrain}
        onUndrain={props.onUndrain}
        onRemove={props.onRemove}
        notify={props.notify}
        onWritten={props.onWritten}
        token={props.token}
        setLimit={props.setLimit}
        set={props.set}
        views={props.views}
      />
    </Panel>
  );
}
